import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { ChevronRight } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import {
  guidanceTargetForNavGroup,
  guidanceTargetForPath,
} from '@/core/guidance/guidance-target-for-path';
import { useIntercom } from '@/core/hooks/use-intercom';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { PlanLockBadge } from '@/core/components/billing/upgrade-surfaces';

import { checkIsActive } from './nav-active';
import { NAV_ATTENTION_BADGE_CLASS } from './nav-attention';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './sidebar';
import {
  type NavCollapsible,
  type NavGroup as NavGroupProps,
  type NavItem,
  type NavLink,
} from './types';

export function NavGroup({ title, items }: NavGroupProps) {
  const { state, isMobile } = useSidebar();
  const location = useLocation();
  const pathnameString = location.pathname;
  const searchString = location.search;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const key = `${item.title}-${item.url || 'collapsible'}`;

          if (!item.items) {
            return (
              <SidebarMenuLink
                key={key}
                item={item}
                pathname={pathnameString}
                search={searchString}
              />
            );
          }

          if (state === 'collapsed' && !isMobile) {
            return (
              <SidebarMenuCollapsedDropdown
                key={key}
                item={item}
                pathname={pathnameString}
                search={searchString}
              />
            );
          }

          return (
            <SidebarMenuCollapsible
              key={key}
              item={item}
              pathname={pathnameString}
              search={searchString}
            />
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="default" className={NAV_ATTENTION_BADGE_CLASS}>
      {children}
    </Badge>
  );
}

/**
 * Sidebar section links use Ionic root+replace instead of react-router push.
 */
const SidebarLink = forwardRef<
  HTMLAnchorElement,
  {
    to: string;
    children: ReactNode;
  } & React.AnchorHTMLAttributes<HTMLAnchorElement>
>(function SidebarLink({ to, onClick, children, className, ...rest }, ref) {
  const { navigateFromSidebar } = useNavigateShell();

  return (
    <a
      ref={ref}
      href={to}
      className={className}
      onClick={(event) => {
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        ) {
          return;
        }

        event.preventDefault();
        navigateFromSidebar(to);
        onClick?.(event);
      }}
      {...rest}
    >
      {children}
    </a>
  );
});

function SidebarMenuLink({
  item,
  pathname,
  search,
}: {
  item: NavLink;
  pathname: string;
  search: string;
}) {
  const { setOpenMobile } = useSidebar();
  const intercom = useIntercom();

  if (item.url === '#support') {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={false} tooltip={item.title}>
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              setOpenMobile(false);
              void intercom.openSupport();
            }}
          >
            {item.icon && <item.icon />}
            <span className="min-w-0 truncate">{item.title}</span>
            {item.requiredFeature ? (
              <PlanLockBadge feature={item.requiredFeature} />
            ) : null}
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
          </button>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={checkIsActive(pathname, item, search)}
        tooltip={item.title}
      >
        <SidebarLink
          to={item.url}
          onClick={() => setOpenMobile(false)}
          {...(guidanceTargetForPath(item.url)
            ? { 'data-guidance-target': guidanceTargetForPath(item.url) }
            : {})}
        >
          {item.icon && <item.icon />}
          <span className="min-w-0 truncate">{item.title}</span>
          {item.requiredFeature ? (
            <PlanLockBadge feature={item.requiredFeature} />
          ) : null}
          {item.badge && <NavBadge>{item.badge}</NavBadge>}
        </SidebarLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function SidebarMenuCollapsible({
  item,
  pathname,
  search,
}: {
  item: NavCollapsible;
  pathname: string;
  search: string;
}) {
  const { setOpenMobile } = useSidebar();
  const guidanceTarget = guidanceTargetForNavGroup(
    item.items.map((subItem) => subItem.url),
  );
  return (
    <Collapsible
      asChild
      defaultOpen={checkIsActive(pathname, item, search)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            {...(guidanceTarget
              ? { 'data-guidance-target': guidanceTarget }
              : {})}
          >
            {item.icon && <item.icon />}
            <span className="min-w-0 truncate">{item.title}</span>
            {item.requiredFeature ? (
              <PlanLockBadge feature={item.requiredFeature} />
            ) : null}
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="CollapsibleContent">
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={checkIsActive(pathname, subItem, search)}
                >
                  <SidebarLink
                    to={subItem.url}
                    onClick={() => setOpenMobile(false)}
                  >
                    {subItem.icon && <subItem.icon />}
                    <span className="min-w-0 truncate">{subItem.title}</span>
                    {subItem.requiredFeature ? (
                      <PlanLockBadge feature={subItem.requiredFeature} />
                    ) : null}
                    {subItem.badge && <NavBadge>{subItem.badge}</NavBadge>}
                  </SidebarLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarMenuCollapsedDropdown({
  item,
  pathname,
  search,
}: {
  item: NavCollapsible;
  pathname: string;
  search: string;
}) {
  const guidanceTarget = guidanceTargetForNavGroup(
    item.items.map((sub) => sub.url),
  );
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={checkIsActive(pathname, item, search)}
            {...(guidanceTarget
              ? { 'data-guidance-target': guidanceTarget }
              : {})}
          >
            {item.icon && <item.icon />}
            <span className="min-w-0 truncate">{item.title}</span>
            {item.requiredFeature ? (
              <PlanLockBadge feature={item.requiredFeature} />
            ) : null}
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ''}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((sub) => (
            <DropdownMenuItem key={`${sub.title}-${sub.url}`} asChild>
              <SidebarLink
                to={sub.url}
                className={`${checkIsActive(pathname, sub, search) ? 'bg-secondary' : ''}`}
              >
                {sub.icon && <sub.icon />}
                <span className="max-w-52 text-wrap">{sub.title}</span>
                {sub.requiredFeature ? (
                  <PlanLockBadge feature={sub.requiredFeature} />
                ) : null}
                {sub.badge && <NavBadge>{sub.badge}</NavBadge>}
              </SidebarLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
